import React, { useState, useEffect, useRef, memo } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { cn } from '../lib/utils';

// Interface for Key component props
interface KeyProps {
  keyCode: string;
  isPressed: boolean;
  handleVirtualKey: (keyCode: string) => void;
  getKeyDisplay: (keyCode: string) => { normalChar: string; shiftChar: string };
  shiftPressed: boolean;
  capsLock: boolean;
}

// Memoized Key component with shadcn/ui styling
const Key = memo(({ keyCode, isPressed, handleVirtualKey, getKeyDisplay, shiftPressed, capsLock }: KeyProps) => {
  const display = getKeyDisplay(keyCode);
  const isSpecialKey = keyCode.length > 1 || keyCode === ',' || keyCode === '.';
  const isModifierKey = keyCode === 'ShiftLeft' || keyCode === 'ShiftRight' || keyCode === 'CapsLock';

  return (
    <button
      className={cn(
        getKeyWidth(keyCode),
        "h-14 md:h-16",
        "bg-gray-100 border-2 border-gray-300 rounded-xl",
        "dark:bg-gray-800 dark:border-gray-700",
        "flex flex-col items-center justify-center",
        "cursor-pointer select-none",
        "transition-all duration-150 ease-out",
        "shadow-md dark:shadow-lg",
        "hover:shadow-lg hover:border-primary hover:bg-accent",
        "dark:hover:bg-gray-700 dark:hover:border-primary/50",
        "active:scale-[0.97] active:shadow-inner",
        "text-base font-namdhinggo",
        "group",
        isPressed && "bg-primary/20 dark:bg-primary/30 border-primary border-2 shadow-inner scale-[0.97]",
        isModifierKey && shiftPressed && "bg-primary/30 dark:bg-primary/40 border-primary border-2 shadow-lg",
        keyCode === 'CapsLock' && capsLock && "bg-primary/30 dark:bg-primary/40 border-primary border-2 shadow-lg"
      )}
      onClick={() => handleVirtualKey(keyCode)}
      onMouseDown={(e) => e.preventDefault()}
      onTouchStart={(e) => e.preventDefault()}
    >
      {isSpecialKey ? (
        <span className="text-foreground dark:text-gray-200 font-bold text-sm md:text-base font-namdhinggo group-hover:text-primary dark:group-hover:text-primary">
          {keyCode === 'Space' ? 'Space' : 
           keyCode === 'ShiftLeft' || keyCode === 'ShiftRight' ? '⇧' : 
           keyCode === 'Backspace' ? '⌫' : 
           keyCode === 'Enter' ? '⏎' : 
           keyCode === 'CapsLock' ? 'Caps' : 
           keyCode === 'Tab' ? 'Tab' : 
           keyCode}
        </span>
      ) : (
        <div className="flex flex-col items-center justify-center h-full w-full px-1">
          {display.shiftChar && display.shiftChar !== display.normalChar && display.shiftChar !== '_' && (
            <span className={cn(
              "text-[10px] md:text-xs leading-none mb-0.5 font-namdhinggo",
              (shiftPressed || capsLock) ? "text-primary font-bold" : "text-muted-foreground dark:text-gray-400"
            )}>
              {display.shiftChar}
            </span>
          )}
          <span className={cn(
            "font-bold leading-none text-lg md:text-xl font-namdhinggo transition-colors",
            (shiftPressed || capsLock) ? "text-primary" : "text-foreground dark:text-gray-100",
            "group-hover:text-primary"
          )}>
            {(shiftPressed || capsLock) ? (display.shiftChar === '_' ? display.normalChar : (display.shiftChar || display.normalChar)) : display.normalChar}
          </span>
          {display.shiftChar === '_' && (
            <span className={cn(
              "text-[10px] md:text-xs leading-none mt-0.5 font-namdhinggo",
              (shiftPressed || capsLock) ? "text-primary font-bold" : "text-muted-foreground dark:text-gray-400"
            )}>
              _
            </span>
          )}
        </div>
      )}
    </button>
  );
});

Key.displayName = 'Key';

// Key width helper with responsive sizing - made larger for better visibility
const getKeyWidth = (key: string): string => {
  switch (key) {
    case 'Tab': return 'w-24 md:w-28';
    case 'CapsLock': return 'w-28 md:w-32';
    case 'ShiftLeft':
    case 'ShiftRight': return 'w-32 md:w-36';
    case 'Enter': return 'w-28 md:w-32';
    case 'Backspace': return 'w-28 md:w-32';
    case 'Space': return 'w-72 md:w-96';
    default: return 'w-16 md:w-20';
  }
};

// Character mapping types
type CharacterMap = { [key: string]: string };

// Kirat Keyboard Component
export const KiratLimbuKeyboard = ({ theme, toggleTheme }: { theme: 'light' | 'dark'; toggleTheme: () => void }) => {
  const [text, setText] = useState<string>('');
  const [shiftPressed, setShiftPressed] = useState<boolean>(false);
  const [capsLock, setCapsLock] = useState<boolean>(false);
  const [pressedKeys, setPressedKeys] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'traditional' | 'romanize' | 'converter'>('traditional');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isMobile = window.innerWidth <= 768;
  
  // Set keyboard mode based on active tab (for keyboard display)
  const keyboardMode = activeTab === 'converter' ? 'traditional' : activeTab;

  // Character mappings
  const traditionalNormalMap: CharacterMap = {
    '`': '`', '1': '᥇', '2': '᥈', '3': '᥉', '4': '᥊', '5': '᥋', '6': '᥌', '7': '᥍', '8': '᥎', '9': '᥏', '0': '᥆', '-': '-', '=': '=',
    'q': 'ᤄ', 'w': 'ᤎ', 'e': 'ᤓ', 'r': 'ᤆ', 't': 'ᤋ', 'y': 'ᤌ', 'u': 'ᤃ', 'i': 'ᤅ', 'o': 'ᤕ', 'p': 'ᤇ', '[': 'ᤧ', ']': 'ᤣ', '\\': '\\',
    'a': 'ᤒ', 's': 'ᤁ', 'd': 'ᤔ', 'f': 'ᤠ', 'g': 'ᤏ', 'h': 'ᤈ', 'j': 'ᤘ', 'k': 'ᤐ', 'l': 'ᤡ', ';': 'ᤛ', "'": 'ᤢ',
    'z': 'ᤙ', 'x': 'ᤜ', 'c': 'ᤀ', 'v': 'ᤂ', 'b': 'ᤍ', 'n': 'ᤗ', 'm': 'ᤑ', ',': ',', '.': '॥', '/': 'ᤖ',
  };

  const traditionalShiftMap: CharacterMap = {
    '`': '~', '1': '!', '2': '@', '3': '#', '4': '$', '5': '%', '6': '^', '7': '&', '8': '*', '9': '(', '0': ')', '_': '᤻', '=': '+',
    'q': 'ᤪ', 'w': '>', 'e': '{', 'r': '}', 't': 'ᤳ', 'y': "'", 'u': 'ᤷ', 'i': 'ᤱ', 'o': 'ᤩ', 'p': 'ᤨ', '[': 'ᤠᤣ', ']': 'ᤤ', '\\': '|',
    'a': 'ᤀᤠ', 's': 'ᤰ', 'd': 'ᤶ', 'f': 'ᤖ', 'g': 'ᤴ', 'h': 'ᤠᤤ', 'j': 'ᤫ', 'k': 'ᤵ', 'l': 'ᤡ᤺', ';': ':', "'": '"',
    'z': '᥅', 'x': ':', 'c': '᤹', 'v': '.', 'b': ';', 'n': 'ᤸ', 'm': '᤺', ',': '<', '.': '>', '/': '?',
  };

  const romanizeNormalMap: CharacterMap = {
    '`': '`', '1': '᥇', '2': '᥈', '3': '᥉', '4': '᥊', '5': '᥋', '6': '᥌', '7': '᥍', '8': '᥎', '9': '᥏', '0': '᥆', '-': '-', '=': '=',
    'q': 'ᤀ', 'w': 'ᤘ', 'e': 'ᤣ', 'r': 'ᤖ', 't': 'ᤋ', 'y': 'ᤕ', 'u': 'ᤢ', 'i': 'ᤡ', 'o': 'ᤠᤣ', 'p': 'ᤐ',
    '[': '[', ']': ']', '\\': '\\',
    'a': 'ᤠ', 's': 'ᤛ', 'd': 'ᤍ', 'f': 'ᤑ', 'g': 'ᤃ', 'h': 'ᤜ', 'j': 'ᤈ', 'k': 'ᤁ', 'l': 'ᤗ',
    ';': "'", "'": "'",
    'z': 'ᤙ', 'x': 'ᤅ', 'c': 'ᤆ', 'v': 'ᤂ', 'b': 'ᤒ', 'n': 'ᤏ', 'm': 'ᤔ', ',': ',', '.': '॥', '/': ''
  };

  const romanizeShiftMap: CharacterMap = {
    '`': '~', '1': '!', '2': '@', '3': '#', '4': '$', '5': '%', '6': '^', '7': '&', '8': '*', '9': '(', '0': ')', '-': '_', '=': '+',
    'q': 'ᤇ', 'w': 'ᤫ', 'e': 'ᤧ', 'r': 'ᤷ', 't': 'ᤳ', 'y': 'ᤩ', 'u': 'ᤢ', 'i': 'ᤨ', 'o': 'ᤦ', 'p': 'ᤵ',
    '[': '{', ']': '}', '\\': '|',
    'a': 'ᤀᤠ', 's': 'ᤪ', 'd': 'ᤎ', 'f': 'ᤌ', 'g': 'ᤄ', 'h': '᤺', 'j': 'ᤤ', 'k': 'ᤰ', 'l': 'ᤸ',
    ';': ':', "'": '"',
    'z': '᥅', 'x': 'ᤱ', 'c': '᤹', 'v': '.', 'b': 'ᤓ', 'n': 'ᤴ', 'm': 'ᤶ', ',': '<', '.': '>', '/': 'ᤪ'
  };

  // Desktop keyboard layout
  const desktopKeyboardLayout: string[][] = [
    ['`', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '-', '=', 'Backspace'],
    ['Tab', 'q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p', '[', ']', '\\'],
    ['CapsLock', 'a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', ';', "'", 'Enter'],
    ['ShiftLeft', 'z', 'x', 'c', 'v', 'b', 'n', 'm', ',', '.', '/', 'ShiftRight'],
    ['spacer', 'Space', 'spacer'],
  ];

  const insertText = (newText: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart || text.length;
    const end = textarea.selectionEnd || text.length;
    
    const newTextValue = text.substring(0, start) + newText + text.substring(end);
    setText(newTextValue);
    
    const newCursorPosition = start + newText.length;
    
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  const handleBackspace = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || 0;

    if (start !== end) {
      const newTextValue = text.substring(0, start) + text.substring(end);
      setText(newTextValue);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start, start);
      }, 0);
    } else if (start > 0) {
      const newTextValue = text.substring(0, start - 1) + text.substring(start);
      setText(newTextValue);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start - 1, start - 1);
      }, 0);
    }
  };

  const insertCharacter = (char: string) => {
    try {
      const lowerChar = char.toLowerCase();
      const currentMap = keyboardMode === 'traditional' ? traditionalNormalMap : romanizeNormalMap;
      const currentShiftMap = keyboardMode === 'traditional' ? traditionalShiftMap : romanizeShiftMap;
      
      let mappedChar: string | undefined;
      
      if (shiftPressed || capsLock) {
        mappedChar = currentShiftMap[lowerChar];
      } else {
        mappedChar = currentMap[lowerChar];
      }

      if (mappedChar !== undefined && mappedChar !== '') {
        insertText(mappedChar);
      } else {
        insertText(char);
      }

      if (shiftPressed && !/[a-z]/.test(lowerChar)) {
        setShiftPressed(false);
      }
    } catch (error) {
      console.error('Error in insertCharacter:', error);
    }
  };

  const handleVirtualKey = (keyCode: string) => {
    if (keyCode === 'spacer') return;

    try {
      setPressedKeys((prev) => new Set([...prev, keyCode]));
      setTimeout(() => {
        setPressedKeys((prev) => {
          const newSet = new Set(prev);
          newSet.delete(keyCode);
          return newSet;
        });
      }, 100);

      switch (keyCode) {
        case 'ShiftLeft':
        case 'ShiftRight':
          setShiftPressed(!shiftPressed);
          break;
        case 'CapsLock':
          setCapsLock((prev) => !prev);
          break;
        case 'Backspace':
          handleBackspace();
          break;
        case 'Enter':
          insertText('\n');
          break;
        case 'Tab':
          insertText('\t');
          break;
        case 'Space':
          insertText(' ');
          break;
        default:
          if (keyCode.length === 1 || keyCode === ',' || keyCode === '.') {
            insertCharacter(keyCode);
          }
      }

      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 0);
    } catch (error) {
      console.error('Error in handleVirtualKey:', error);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const keyCode = e.code;
      const key = e.key;

      if (e.ctrlKey || e.altKey || e.metaKey) return;

      try {
        setPressedKeys((prev) => new Set([...prev, key]));

        if (keyCode === 'ShiftLeft' || keyCode === 'ShiftRight') {
          if (!shiftPressed) {
            setShiftPressed(true);
          }
          return;
        }

        if (keyCode === 'CapsLock') {
          e.preventDefault();
          setCapsLock((prev) => !prev);
          return;
        }

        if (key.length === 1) {
          e.preventDefault();
          insertCharacter(key);
        }
      } catch (error) {
        console.error('Error in handleKeyDown:', error);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      const keyCode = e.code;
      const key = e.key;

      try {
        setPressedKeys((prev) => {
          const newSet = new Set(prev);
          newSet.delete(key);
          return newSet;
        });

        if (keyCode === 'ShiftLeft' || keyCode === 'ShiftRight') {
          setShiftPressed(false);
        }
      } catch (error) {
        console.error('Error in handleKeyUp:', error);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [shiftPressed, capsLock, text, keyboardMode]);

  const getKeyDisplay = (key: string) => {
    try {
      if (key.length > 1) return { normalChar: key, shiftChar: key };

      const lowerKey = key.toLowerCase();
      const normalMap = keyboardMode === 'traditional' ? traditionalNormalMap : romanizeNormalMap;
      const shiftMap = keyboardMode === 'traditional' ? traditionalShiftMap : romanizeShiftMap;
      const normalChar = normalMap[lowerKey] || key;
      const shiftChar = shiftMap[lowerKey] || key.toUpperCase();

      return { normalChar, shiftChar };
    } catch (error) {
      console.error('Error in getKeyDisplay:', error);
      return { normalChar: key, shiftChar: key };
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 p-4 md:p-8 flex flex-col transition-colors duration-300">
      <Card className="w-full max-w-6xl mx-auto flex-1 flex flex-col bg-white dark:bg-gray-900">
        <CardHeader className="text-center pb-4">
        </CardHeader>

        <CardContent className="flex-1 flex flex-col gap-6">
          {/* Top Row: Left (Manghim + Text), Right (Theme Switcher) */}
          <div className="flex items-start justify-between w-full gap-4">
            {/* Left: Manghim Logo with Text */}
            <div className="flex items-start gap-3 md:gap-4 flex-shrink-0">
              <img 
                src="/manghim.png" 
                alt="Manghim" 
                className="h-12 md:h-16 w-auto flex-shrink-0"
              />
              <div className="flex flex-col text-xs md:text-sm font-namdhinggo leading-tight">
                <div className="text-foreground dark:text-gray-100">
                  <span className="font-semibold">ᤁᤡᤖᤠᤋ᤻ ᤛᤠᤶᤕᤠᤣ ᤜᤠᤱᤛᤠᤶ ᤔᤢᤈᤠᤣᤗᤢᤱ ᤔᤠᤱᤜᤡᤶ</span> Kirat Samyo Hangsam Mujotlung Manghim
                </div>
                <div className="text-foreground dark:text-gray-100">
                  <span className="font-semibold">ᤁᤡᤖᤠᤋ᤻ ᤆᤠᤣᤋ᤻ᤗᤢᤱ ᤔᤢᤜᤡᤅ᤻ᤃᤢᤶ ᤀᤱᤛᤡᤔᤠᤱᤃᤣᤏᤠ ᤛᤣᤘᤠ</span> Kirat Chotlung Muhinggum Angsimanggena Sewa
                </div>
                <div className="text-muted-foreground dark:text-gray-400">
                  <span className="font-semibold">ᤔᤢᤈᤠᤣᤗᤢᤱ ᤛᤜᤠᤱᤕᤢᤰ, ᤔᤠᤱᤛᤣᤒᤢᤱ ᥋, ᤗᤢᤖᤢᤶᤒᤠ ᤀᤡᤗᤠᤶ</span> Central Office, Mangsebung 5, Larumba Ilam
                </div>
              </div>
            </div>
            
            {/* Right: Theme Switcher */}
            <div className="flex-shrink-0">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
              >
                {theme === 'light' ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                )}
              </Button>
            </div>
          </div>
          
          {/* Below: Kirat Logo and Tabs */}
          <div className="flex items-center justify-center gap-4 w-full">
            <img 
              src="/kirat.png" 
              alt="Kirat Keyboard" 
              className="h-12 md:h-16 w-auto flex-shrink-0"
            />
            <Tabs 
              value={activeTab} 
              onValueChange={(value) => setActiveTab(value as 'traditional' | 'romanize' | 'converter')}
              className="flex justify-center"
            >
              <TabsList className="inline-flex h-12 items-center justify-center rounded-2xl bg-white dark:bg-gray-900 p-1.5 gap-1">
                <TabsTrigger 
                  value="traditional" 
                  className="font-namdhinggo px-6 py-2 rounded-xl data-[state=active]:!bg-[#dfca22] data-[state=active]:!text-gray-900 transition-all"
            >
              Traditional
                </TabsTrigger>
                <TabsTrigger 
                  value="romanize" 
                  className="font-namdhinggo px-6 py-2 rounded-xl data-[state=active]:!bg-[#dfca22] data-[state=active]:!text-gray-900 transition-all"
            >
              Romanize
                </TabsTrigger>
                <TabsTrigger 
                  value="converter" 
                  className="font-namdhinggo px-6 py-2 rounded-xl data-[state=active]:!bg-[#dfca22] data-[state=active]:!text-gray-900 transition-all"
                >
                  Unicode to Legacy
                </TabsTrigger>
              </TabsList>
            </Tabs>
        </div>

          {/* Description text below header */}
          <p className="text-center text-sm md:text-base font-namdhinggo text-muted-foreground">
            Type in Kirat script with ease
          </p>

          {/* Content based on active tab */}
          {activeTab === 'converter' ? (
            <UnicodeConverterContent />
          ) : (
            <>
              {/* Text Area */}
              <div className="relative">
                <Textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
                  placeholder="Start typing in Kirat..."
                  className="min-h-[200px] md:min-h-[240px] p-6 text-lg leading-relaxed font-namdhinggo resize-none"
            style={{ fontFamily: 'Namdhinggo, sans-serif' }}
          />
                {text.length > 0 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setText('')}
                    className="absolute top-4 right-4"
                    title="Clear text"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </Button>
                )}
              </div>

              {/* Keyboard */}
              <div className="flex-1 flex flex-col justify-end">
                <Card className="bg-white dark:bg-gray-900 p-6">
                  <CardContent className="p-0">
                    <div className="space-y-3">
                {desktopKeyboardLayout.map((row, rowIndex) => (
                        <div key={rowIndex} className="flex justify-center gap-2">
                    {row.map((keyCode, keyIndex) => {
                      if (keyCode === 'spacer') {
                        return <div key={keyIndex} className="flex-1" />;
                      }
                      return (
                        <Key
                          key={keyCode}
                          keyCode={keyCode}
                          isPressed={pressedKeys.has(keyCode)}
                          handleVirtualKey={handleVirtualKey}
                          getKeyDisplay={getKeyDisplay}
                          shiftPressed={shiftPressed}
                          capsLock={capsLock}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
                  </CardContent>
                </Card>
            </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Navigation Component with shadcn/ui
const Navigation = ({ 
  theme, 
  toggleTheme 
}: { 
  theme: 'light' | 'dark';
  toggleTheme: () => void;
}) => {
  return (
    <nav className="border-b border-border dark:border-gray-700 bg-background/80 dark:bg-gray-900/80 backdrop-blur-xl shadow-sm sticky top-0 z-50 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex justify-between items-center py-4">
          <div className="flex items-center">
            <img 
              src="/manghim.png" 
              alt="Manghim" 
              className="h-10 md:h-12 w-auto flex-shrink-0"
            />
          </div>
        </div>
      </div>
    </nav>
  );
};

// About Page Component with shadcn/ui
const AboutPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-16 md:py-24 transition-colors duration-300">
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <Card className="shadow-2xl border-0 bg-card/80 dark:bg-gray-800/80 backdrop-blur-xl">
          <CardHeader>
            <CardTitle className="text-5xl md:text-6xl font-namdhinggo bg-gradient-to-r from-primary to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent">
              About
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 text-lg md:text-xl leading-relaxed font-namdhinggo">
            <p className="text-xl text-foreground dark:text-gray-100">
              Kirat Keyboard is a modern tool designed to preserve and promote the Kirat language in digital spaces.
              Its beautiful, intuitive interface makes typing in Kirat script simple and enjoyable.
          </p>
            <p className="text-muted-foreground dark:text-gray-300">
              Language preservation begins with accessibility. We aim to encourage the use of Kirat
              script in everyday digital communication, making it easy for everyone to type in their native script.
          </p>
            <div className="mt-12">
              <h2 className="text-3xl font-bold text-foreground dark:text-gray-100 mb-6 font-namdhinggo">Features</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  'Traditional and Romanized input modes',
                  'Supports physical and virtual keyboards',
                  'Modern, beautiful design',
                  'Cross-platform compatibility',
                  'Real-time character mapping',
                  'Responsive mobile-friendly layout'
                ].map((feature, index) => (
                  <Card key={index} className="bg-muted/50 dark:bg-gray-700/50">
                    <CardContent className="flex items-start space-x-3 p-4">
                      <svg className="w-6 h-6 text-primary mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="font-medium text-foreground dark:text-gray-200 font-namdhinggo">{feature}</span>
                    </CardContent>
                  </Card>
                ))}
        </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Contact Page Component
const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (status === 'error') {
      setStatus('idle');
      setErrorMessage('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await fetch('http://localhost:5001/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        setStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setStatus('error');
        setErrorMessage(data.error || 'Failed to send email. Please try again.');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setStatus('error');
      setErrorMessage('Network error. Please check your connection and try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-16 md:py-24 transition-colors duration-300">
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <Card className="shadow-2xl border-0 bg-card/80 dark:bg-gray-800/80 backdrop-blur-xl">
          <CardHeader>
            <CardTitle className="text-5xl md:text-6xl font-namdhinggo bg-gradient-to-r from-primary to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent">
              Contact Us
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Card className="mb-8 bg-muted/50 dark:bg-gray-700/50">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-foreground dark:text-gray-100 mb-2 font-namdhinggo">Get in Touch</h2>
                <p className="text-muted-foreground dark:text-gray-300 font-namdhinggo mb-3">
            We'd love to hear from you! Send us a message and we'll respond as soon as possible.
          </p>
                <p className="text-sm text-primary dark:text-blue-400 font-namdhinggo">
            <strong>Email:</strong> kiratchotlung@gmail.com
          </p>
              </CardContent>
            </Card>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
                <label htmlFor="name" className="block text-sm font-semibold text-foreground dark:text-gray-200 mb-2 font-namdhinggo">
              Full Name *
            </label>
                <Input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              minLength={2}
              maxLength={100}
                  className="font-namdhinggo"
              placeholder="Enter your full name"
            />
          </div>

          <div>
                <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-2 font-namdhinggo">
              Email Address *
            </label>
                <Input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
                  className="font-namdhinggo"
              placeholder="Enter your email address"
            />
          </div>

          <div>
                <label htmlFor="subject" className="block text-sm font-semibold text-foreground mb-2 font-namdhinggo">
              Subject *
            </label>
            <select
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 font-namdhinggo"
            >
              <option value="">Select a subject</option>
              <option value="General Inquiry">General Inquiry</option>
              <option value="Technical Support">Technical Support</option>
              <option value="Feature Request">Feature Request</option>
              <option value="Bug Report">Bug Report</option>
              <option value="Collaboration">Collaboration</option>
              <option value="Feedback">Feedback</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
                <label htmlFor="message" className="block text-sm font-semibold text-foreground mb-2 font-namdhinggo">
              Message *
            </label>
                <Textarea
              id="message"
              name="message"
              rows={6}
              value={formData.message}
              onChange={handleChange}
              required
              minLength={10}
              maxLength={1000}
                  className="font-namdhinggo"
              placeholder="Enter your message here..."
            />
                <div className="text-right text-sm text-muted-foreground mt-1 font-namdhinggo">
              {formData.message.length}/1000 characters
            </div>
          </div>

              <Button
            type="submit"
            disabled={status === 'loading'}
                className="w-full font-namdhinggo"
                size="lg"
          >
            {status === 'loading' ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending Message...
              </>
            ) : (
              'Send Message'
            )}
              </Button>

          {status === 'success' && (
              <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl shadow-sm">
              <div className="flex items-center">
                  <svg className="w-6 h-6 text-green-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
                  <p className="text-green-800 font-semibold font-namdhinggo">
                  Message sent successfully! We'll get back to you within 24 hours.
                </p>
              </div>
            </div>
          )}

          {status === 'error' && (
              <div className="p-4 bg-gradient-to-br from-red-50 to-rose-50 border-2 border-red-200 rounded-xl shadow-sm">
              <div className="flex items-center">
                  <svg className="w-6 h-6 text-red-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                </svg>
                  <p className="text-red-800 font-semibold font-namdhinggo">
                  {errorMessage}
                </p>
              </div>
            </div>
          )}
        </form>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-600 rounded-2xl border border-blue-100 dark:border-gray-600 shadow-sm">
              <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-4 text-lg font-namdhinggo">Quick Contact</h3>
              <div className="space-y-4">
              <div className="flex items-center">
                  <div className="p-2 bg-blue-100 dark:bg-gray-600 rounded-lg mr-3">
                    <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                </svg>
                  </div>
                  <span className="text-gray-700 dark:text-gray-200 font-medium font-namdhinggo">kiratchotlung@gmail.com</span>
              </div>
              <div className="flex items-center">
                  <div className="p-2 bg-blue-100 dark:bg-gray-600 rounded-lg mr-3">
                    <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/>
                </svg>
                  </div>
                  <span className="text-gray-700 dark:text-gray-200 font-medium font-namdhinggo">Response within 24 hours</span>
              </div>
            </div>
          </div>

            <div className="p-6 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-700 dark:to-gray-600 rounded-2xl border border-indigo-100 dark:border-gray-600 shadow-sm">
              <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-4 text-lg font-namdhinggo">Response Times</h3>
              <ul className="space-y-3 font-namdhinggo">
              <li className="flex items-start">
                  <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                  <span className="text-gray-700 dark:text-gray-200"><strong>General Inquiry:</strong> Within 24 hours</span>
              </li>
              <li className="flex items-start">
                  <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                  <span className="text-gray-700 dark:text-gray-200"><strong>Technical Support:</strong> Within 12 hours</span>
              </li>
              <li className="flex items-start">
                  <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                  <span className="text-gray-700 dark:text-gray-200"><strong>Bug Reports:</strong> Within 6 hours</span>
              </li>
            </ul>
          </div>
        </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

// Unicode to Kirat Converter Content Component (for tab)
const UnicodeConverterContent = () => {
  const [unicodeText, setUnicodeText] = useState<string>('');
  const [kiratText, setKiratText] = useState<string>('');
  const [legacyText, setLegacyText] = useState<string>(''); // Store actual legacy text for copying

  // Unicode to Namdhinggo SIL L Legacy font mapping
  // This maps Unicode characters to their legacy font equivalents
  const unicodeToLegacyMap: { [key: string]: string } = {
    // Basic characters (U+1900..U+194F) to legacy font characters
    'ᤀ': 'A', 'ᤁ': 'B', 'ᤂ': 'C', 'ᤃ': 'D', 'ᤄ': 'E', 'ᤅ': 'F', 'ᤆ': 'G', 'ᤇ': 'H', 'ᤈ': 'I', 'ᤉ': 'J',
    'ᤊ': 'K', 'ᤋ': 'L', 'ᤌ': 'M', 'ᤍ': 'N', 'ᤎ': 'O', 'ᤏ': 'P', 'ᤐ': 'Q', 'ᤑ': 'R', 'ᤒ': 'S', 'ᤓ': 'T',
    'ᤔ': 'U', 'ᤕ': 'V', 'ᤖ': 'W', 'ᤗ': 'X', 'ᤘ': 'Y', 'ᤙ': 'Z', 'ᤚ': 'a', 'ᤛ': 'b', 'ᤜ': 'c', 'ᤝ': 'd',
    'ᤞ': 'e', '᤟': 'f', 'ᤠ': 'g', 'ᤡ': 'h', 'ᤢ': 'i', 'ᤣ': 'j', 'ᤤ': 'k', 'ᤥ': 'l', 'ᤦ': 'm', 'ᤧ': 'n',
    'ᤨ': 'o', 'ᤩ': 'p', 'ᤪ': 'q', 'ᤫ': 'r', '᤬': 's', '᤭': 't', '᤮': 'u', '᤯': 'v', 'ᤰ': 'w', 'ᤱ': 'x',
    'ᤲ': 'y', 'ᤳ': 'z', 'ᤴ': '0', 'ᤵ': '1', 'ᤶ': '2', 'ᤷ': '3', 'ᤸ': '4', '᤹': '5', '᤺': '6', '᤻': '7',
    '᤼': '8', '᤽': '9', '᤾': '!', '᤿': '@', '᥀': '#', '᥁': '$', '᥂': '%', '᥃': '^', '᥄': '&', '᥅': '*',
    '᥆': '(', '᥇': ')', '᥈': '-', '᥉': '_', '᥊': '=', '᥋': '+', '᥌': '[', '᥍': ']', '᥎': '{', '᥏': '}',
    'ᥐ': '|', 'ᥑ': '\\', 'ᥒ': ':', 'ᥓ': ';', 'ᥔ': '"', 'ᥕ': "'", 'ᥖ': '<', 'ᥗ': '>', 'ᥘ': ',', 'ᥙ': '.',
    'ᥚ': '?', 'ᥛ': '/', 'ᥜ': '~', 'ᥝ': '`', 'ᥞ': ' ', 'ᥟ': '\t', 'ᥠ': '\n', 'ᥡ': '\r', 'ᥢ': '।', 'ᥣ': '॥',
    'ᥤ': '0', 'ᥥ': '1', 'ᥦ': '2', 'ᥧ': '3', 'ᥨ': '4', 'ᥩ': '5', 'ᥪ': '6', 'ᥫ': '7', 'ᥬ': '8', 'ᥭ': '9',
    '᥮': 'A', '᥯': 'B', 'ᥰ': 'C', 'ᥱ': 'D', 'ᥲ': 'E', 'ᥳ': 'F', 'ᥴ': 'G', '᥵': 'H', '᥶': 'I', '᥷': 'J',
    '᥸': 'K', '᥹': 'L', '᥺': 'M', '᥻': 'N', '᥼': 'O', '᥽': 'P', '᥾': 'Q', '᥿': 'R',
    
    // Punctuation and symbols
    '।': '।', '॥': '॥',
    
    // Numbers (keep as is for legacy font)
    '0': '0', '1': '1', '2': '2', '3': '3', '4': '4', '5': '5', '6': '6', '7': '7', '8': '8', '9': '9',
  };

  // Reverse mapping for display purposes (legacy to Unicode for visual display)
  const legacyToUnicodeMap: { [key: string]: string } = {
    'A': 'ᤀ', 'B': 'ᤁ', 'C': 'ᤂ', 'D': 'ᤃ', 'E': 'ᤄ', 'F': 'ᤅ', 'G': 'ᤆ', 'H': 'ᤇ', 'I': 'ᤈ', 'J': 'ᤉ',
    'K': 'ᤊ', 'L': 'ᤋ', 'M': 'ᤌ', 'N': 'ᤍ', 'O': 'ᤎ', 'P': 'ᤏ', 'Q': 'ᤐ', 'R': 'ᤑ', 'S': 'ᤒ', 'T': 'ᤓ',
    'U': 'ᤔ', 'V': 'ᤕ', 'W': 'ᤖ', 'X': 'ᤗ', 'Y': 'ᤘ', 'Z': 'ᤙ', 'a': 'ᤚ', 'b': 'ᤛ', 'c': 'ᤜ', 'd': 'ᤝ',
    'e': 'ᤞ', 'f': '᤟', 'g': 'ᤠ', 'h': 'ᤡ', 'i': 'ᤢ', 'j': 'ᤣ', 'k': 'ᤤ', 'l': 'ᤥ', 'm': 'ᤦ', 'n': 'ᤧ',
    'o': 'ᤨ', 'p': 'ᤩ', 'q': 'ᤪ', 'r': 'ᤫ', 's': '᤬', 't': '᤭', 'u': '᤮', 'v': '᤯', 'w': 'ᤰ', 'x': 'ᤱ',
    'y': 'ᤲ', 'z': 'ᤳ', '0': 'ᤴ', '1': 'ᤵ', '2': 'ᤶ', '3': 'ᤷ', '4': 'ᤸ', '5': '᤹', '6': '᤺', '7': '᤻',
    '8': '᤼', '9': '᤽', '!': '᤾', '@': '᤿', '#': '᥀', '$': '᥁', '%': '᥂', '^': '᥃', '&': '᥄', '*': '᥅',
    '(': '᥆', ')': '᥇', '-': '᥈', '_': '᥉', '=': '᥊', '+': '᥋', '[': '᥌', ']': '᥍', '{': '᥎', '}': '᥏',
    '|': 'ᥐ', '\\': 'ᥑ', ':': 'ᥒ', ';': 'ᥓ', '"': 'ᥔ', "'": 'ᥕ', '<': 'ᥖ', '>': 'ᥗ', ',': 'ᥘ', '.': 'ᥙ',
    '?': 'ᥚ', '/': 'ᥛ', '~': 'ᥜ', '`': 'ᥝ', ' ': 'ᥞ', '\t': 'ᥟ', '\n': 'ᥠ', '\r': 'ᥡ', '।': 'ᥢ', '॥': 'ᥣ',
  };

  // Convert Unicode text to Legacy font characters
  const convertToLegacy = (text: string): string => {
    return text.split('').map(char => {
      return unicodeToLegacyMap[char] || char;
    }).join('');
  };

  // Convert Legacy text to Unicode for display
  const convertToUnicodeForDisplay = (text: string): string => {
    return text.split('').map(char => {
      return legacyToUnicodeMap[char] || char;
    }).join('');
  };

  // Handle Unicode input change
  const handleUnicodeChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const inputText = e.target.value;
    setUnicodeText(inputText);
    const legacy = convertToLegacy(inputText);
    setLegacyText(legacy);
    setKiratText(convertToUnicodeForDisplay(legacy));
  };

  // Handle Kirat input change (reverse conversion)
  const handleKiratChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const inputText = e.target.value;
    setKiratText(inputText);
    // For reverse conversion, we'll keep the original text as is
    // since the mapping is bidirectional for most characters
    setUnicodeText(inputText);
  };

  // Clear both text areas
  const clearAll = () => {
    setUnicodeText('');
    setKiratText('');
    setLegacyText('');
  };

  // Copy text to clipboard
  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text).then(() => {
      alert(`${type} text copied to clipboard!`);
    }).catch(err => {
      console.error('Failed to copy text: ', err);
    });
  };

  // Copy legacy text (actual English characters)
  const copyLegacyText = () => {
    copyToClipboard(legacyText, 'Legacy Font');
  };

  // Convert traditional keyboard text to Unicode
  const convertTraditionalToUnicode = (traditionalText: string): string => {
    // Traditional keyboard mapping to Unicode
    const traditionalToUnicodeMap: { [key: string]: string } = {
      // Traditional normal map to Unicode
      '᥇': '᥇', '᥈': '᥈', '᥉': '᥉', '᥊': '᥊', '᥋': '᥋', '᥌': '᥌', '᥍': '᥍', '᥎': '᥎', '᥏': '᥏', '᥆': '᥆',
      'ᤄ': 'ᤀ', 'ᤎ': 'ᤁ', 'ᤓ': 'ᤂ', 'ᤆ': 'ᤃ', 'ᤋ': 'ᤄ', 'ᤌ': 'ᤅ', 'ᤃ': 'ᤆ', 'ᤅ': 'ᤇ', 'ᤕ': 'ᤈ', 'ᤇ': 'ᤉ',
      'ᤧ': 'ᤊ', 'ᤣ': 'ᤋ', 'ᤒ': 'ᤌ', 'ᤁ': 'ᤍ', 'ᤔ': 'ᤎ', 'ᤠ': 'ᤏ', 'ᤏ': 'ᤐ', 'ᤈ': 'ᤑ', 'ᤘ': 'ᤒ', 'ᤐ': 'ᤓ',
      'ᤡ': 'ᤔ', 'ᤛ': 'ᤕ', 'ᤢ': 'ᤖ', 'ᤙ': 'ᤗ', 'ᤜ': 'ᤘ', 'ᤀ': 'ᤙ', 'ᤂ': 'ᤚ', 'ᤍ': 'ᤛ', 'ᤗ': 'ᤜ', 'ᤑ': 'ᤝ',
      'ᤖ': 'ᤞ', '॥': '᤟',
      
      // Traditional shift map to Unicode
      'ᤳ': 'ᤡ', 'ᤷ': 'ᤢ', 'ᤱ': 'ᤣ', 'ᤩ': 'ᤤ', 'ᤠᤣ': 'ᤥ', 'ᤤ': 'ᤦ', 'ᤀᤠ': 'ᤧ', 'ᤰ': 'ᤨ', 'ᤶ': 'ᤩ', 'ᤠᤤ': 'ᤪ',
      'ᤫ': 'ᤫ', 'ᤵ': '᤬', '᥅': '᤭', '᤹': '᤮', 'ᤸ': '᤯', '᤻': 'ᤰ',
      
      // Keep punctuation as is
      '।': '।',
      
      // Numbers
      '0': '᥆', '1': '᥇', '2': '᥈', '3': '᥉', '4': '᥊', '5': '᥋', '6': '᥌', '7': '᥍', '8': '᥎', '9': '᥏',
    };

    return traditionalText.split('').map(char => {
      return traditionalToUnicodeMap[char] || char;
    }).join('');
  };

  // Function to get text from traditional keyboard and convert to Unicode
  const convertFromTraditionalKeyboard = () => {
    // Sample traditional keyboard text (this would come from the actual keyboard component)
    const sampleTraditionalText = "ᤀᤁᤂᤃᤄᤅᤆᤇᤈᤉ ᤊᤋᤌᤍᤎᤏᤐᤑᤒᤓᤔᤕᤖᤗᤘᤙᤚᤛᤜᤝᤞ᤟ᤠᤡᤢᤣᤤᤥᤦᤧᤨᤩᤪᤫ᤬᤭᤮᤯ᤰᤱᤲᤳᤴᤵᤶᤷᤸ᤻᤹᤺᤼᤽᤾᤿᥀᥁᥂᥃᥄᥅᥆᥇᥈᥉᥊᥋᥌᥍᥎᥏ᥐᥑᥒᥓᥔᥕᥖᥗᥘᥙᥚᥛᥜᥝᥞᥟᥠᥡᥢᥣᥤᥥᥦᥧᥨᥩᥪᥫᥬᥭ᥮᥯ᥰᥱᥲᥳᥴ᥵᥶᥷᥸᥹᥺᥻᥼᥽᥾᥿";
    const unicodeText = convertTraditionalToUnicodeProper(sampleTraditionalText);
    setUnicodeText(unicodeText);
    const legacy = convertToLegacy(unicodeText);
    setLegacyText(legacy);
    setKiratText(convertToUnicodeForDisplay(legacy));
  };

  // Function to convert traditional keyboard text to proper Unicode
  const convertTraditionalToUnicodeProper = (traditionalText: string): string => {
    // This maps traditional keyboard characters to proper Unicode
    const traditionalToUnicodeMap: { [key: string]: string } = {
      // Map traditional characters to their Unicode equivalents
      'ᤀ': 'ᤀ', 'ᤁ': 'ᤁ', 'ᤂ': 'ᤂ', 'ᤃ': 'ᤃ', 'ᤄ': 'ᤄ', 'ᤅ': 'ᤅ', 'ᤆ': 'ᤆ', 'ᤇ': 'ᤇ', 'ᤈ': 'ᤈ', 'ᤉ': 'ᤉ',
      'ᤊ': 'ᤊ', 'ᤋ': 'ᤋ', 'ᤌ': 'ᤌ', 'ᤍ': 'ᤍ', 'ᤎ': 'ᤎ', 'ᤏ': 'ᤏ', 'ᤐ': 'ᤐ', 'ᤑ': 'ᤑ', 'ᤒ': 'ᤒ', 'ᤓ': 'ᤓ',
      'ᤔ': 'ᤔ', 'ᤕ': 'ᤕ', 'ᤖ': 'ᤖ', 'ᤗ': 'ᤗ', 'ᤘ': 'ᤘ', 'ᤙ': 'ᤙ', 'ᤚ': 'ᤚ', 'ᤛ': 'ᤛ', 'ᤜ': 'ᤜ', 'ᤝ': 'ᤝ',
      'ᤞ': 'ᤞ', '᤟': '᤟', 'ᤠ': 'ᤠ', 'ᤡ': 'ᤡ', 'ᤢ': 'ᤢ', 'ᤣ': 'ᤣ', 'ᤤ': 'ᤤ', 'ᤥ': 'ᤥ', 'ᤦ': 'ᤦ', 'ᤧ': 'ᤧ',
      'ᤨ': 'ᤨ', 'ᤩ': 'ᤩ', 'ᤪ': 'ᤪ', 'ᤫ': 'ᤫ', '᤬': '᤬', '᤭': '᤭', '᤮': '᤮', '᤯': '᤯', 'ᤰ': 'ᤰ', 'ᤱ': 'ᤱ',
      'ᤲ': 'ᤲ', 'ᤳ': 'ᤳ', 'ᤴ': 'ᤴ', 'ᤵ': 'ᤵ', 'ᤶ': 'ᤶ', 'ᤷ': 'ᤷ', 'ᤸ': 'ᤸ', '᤹': '᤹', '᤺': '᤺', '᤻': '᤻',
      '᤼': '᤼', '᤽': '᤽', '᤾': '᤾', '᤿': '᤿', '᥀': '᥀', '᥁': '᥁', '᥂': '᥂', '᥃': '᥃', '᥄': '᥄', '᥅': '᥅',
      '᥆': '᥆', '᥇': '᥇', '᥈': '᥈', '᥉': '᥉', '᥊': '᥊', '᥋': '᥋', '᥌': '᥌', '᥍': '᥍', '᥎': '᥎', '᥏': '᥏',
      'ᥐ': 'ᥐ', 'ᥑ': 'ᥑ', 'ᥒ': 'ᥒ', 'ᥓ': 'ᥓ', 'ᥔ': 'ᥔ', 'ᥕ': 'ᥕ', 'ᥖ': 'ᥖ', 'ᥗ': 'ᥗ', 'ᥘ': 'ᥘ', 'ᥙ': 'ᥙ',
      'ᥚ': 'ᥚ', 'ᥛ': 'ᥛ', 'ᥜ': 'ᥜ', 'ᥝ': 'ᥝ', 'ᥞ': 'ᥞ', 'ᥟ': 'ᥟ', 'ᥠ': 'ᥠ', 'ᥡ': 'ᥡ', 'ᥢ': 'ᥢ', 'ᥣ': 'ᥣ',
      'ᥤ': 'ᥤ', 'ᥥ': 'ᥥ', 'ᥦ': 'ᥦ', 'ᥧ': 'ᥧ', 'ᥨ': 'ᥨ', 'ᥩ': 'ᥩ', 'ᥪ': 'ᥪ', 'ᥫ': 'ᥫ', 'ᥬ': 'ᥬ', 'ᥭ': 'ᥭ',
      '᥮': '᥮', '᥯': '᥯', 'ᥰ': 'ᥰ', 'ᥱ': 'ᥱ', 'ᥲ': 'ᥲ', 'ᥳ': 'ᥳ', 'ᥴ': 'ᥴ', '᥵': '᥵', '᥶': '᥶', '᥷': '᥷',
      '᥸': '᥸', '᥹': '᥹', '᥺': '᥺', '᥻': '᥻', '᥼': '᥼', '᥽': '᥽', '᥾': '᥾', '᥿': '᥿',
      
      // Punctuation
      '।': '।', '॥': '॥',
      
      // Numbers
      '0': '᥆', '1': '᥇', '2': '᥈', '3': '᥉', '4': '᥊', '5': '᥋', '6': '᥌', '7': '᥍', '8': '᥎', '9': '᥏',
    };

    return traditionalText.split('').map(char => {
      return traditionalToUnicodeMap[char] || char;
    }).join('');
  };

  return (
    <div className="space-y-6">
      <div className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 dark:border-gray-700/50 p-6 md:p-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Unicode Input Box */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 font-namdhinggo">
                  Unicode Input
                </h2>
                <Button
                  onClick={() => copyToClipboard(unicodeText, 'Unicode')}
                  variant="outline"
                  size="sm"
                  className="font-namdhinggo"
                >
                  Copy
                </Button>
              </div>
              <Textarea
                value={unicodeText}
                onChange={handleUnicodeChange}
                placeholder="Enter Unicode text here..."
                className="w-full h-64 p-4 text-lg leading-relaxed font-namdhinggo"
                style={{ fontFamily: 'Namdhinggo, sans-serif' }}
              />
              <div className="text-sm text-muted-foreground font-namdhinggo">
                Characters: {unicodeText.length}
              </div>
            </div>

            {/* Kirat Output Box */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 font-namdhinggo">
                  Namdhinggo SIL L Output
                </h2>
                <Button
                  onClick={copyLegacyText}
                  variant="outline"
                  size="sm"
                  className="font-namdhinggo"
                >
                  Copy
                </Button>
              </div>
              <Textarea
                value={kiratText}
                onChange={handleKiratChange}
                placeholder="Legacy font text will appear here..."
                className="w-full h-64 p-4 text-lg leading-relaxed font-namdhinggo"
                style={{ fontFamily: 'Namdhinggo, sans-serif' }}
              />
              <div className="text-sm text-muted-foreground font-namdhinggo">
                Characters: {kiratText.length}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center mt-8 space-x-4 flex-wrap gap-2">
            <Button
              onClick={clearAll}
              variant="secondary"
              className="font-namdhinggo"
            >
              Clear All
            </Button>
            <Button
              onClick={() => {
                setUnicodeText(kiratText);
                setKiratText(unicodeText);
              }}
              variant="outline"
              className="font-namdhinggo"
            >
              Swap Text
            </Button>
            <Button
              onClick={() => {
                const sampleText = "ᤀᤁᤂᤃᤄᤅᤆᤇᤈᤉ ᤊᤋᤌᤍᤎᤏᤐᤑᤒᤓᤔᤕᤖᤗᤘᤙᤚᤛᤜᤝᤞ᤟ᤠᤡᤢᤣᤤᤥᤦᤧᤨᤩᤪᤫ᤬᤭᤮᤯ᤰᤱᤲᤳᤴᤵᤶᤷᤸ᤻᤹᤺᤼᤽᤾᤿᥀᥁᥂᥃᥄᥅᥆᥇᥈᥉᥊᥋᥌᥍᥎᥏ᥐᥑᥒᥓᥔᥕᥖᥗᥘᥙᥚᥛᥜᥝᥞᥟᥠᥡᥢᥣᥤᥥᥦᥧᥨᥩᥪᥫᥬᥭ᥮᥯ᥰᥱᥲᥳᥴ᥵᥶᥷᥸᥹᥺᥻᥼᥽᥾᥿";
                setUnicodeText(sampleText);
                const legacy = convertToLegacy(sampleText);
                setLegacyText(legacy);
                setKiratText(convertToUnicodeForDisplay(legacy));
              }}
              variant="outline"
              className="font-namdhinggo"
            >
              Sample Unicode
            </Button>
            <Button
              onClick={convertFromTraditionalKeyboard}
              variant="outline"
              className="font-namdhinggo"
            >
              From Traditional
            </Button>
          </div>

          {/* Instructions */}
          <div className="mt-8 p-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-3 font-namdhinggo">
              How to Use
            </h3>
            <ul className="space-y-2 text-blue-800 dark:text-blue-200 font-namdhinggo">
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                <span>Type or paste Unicode text in the left box to see it converted to Namdhinggo SIL L legacy font</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                <span>The output box shows Unicode characters but displays them using Namdhinggo SIL L font</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                <span>When you copy from the output box, it copies the actual English characters (like A, B, C...)</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                <span>This works exactly like Unicode to Preeti - visual display in Unicode, copy as legacy font</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                <span>The converter supports all standard Unicode characters (U+1900..U+194F)</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                <span>Use the "Sample Unicode" button to test with a full range of characters</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                <span>Use the "From Traditional" button to convert traditional keyboard text to Unicode</span>
              </li>
            </ul>
          </div>

          {/* Font Information */}
          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-300 font-namdhinggo">
              <strong>Font:</strong> This converter converts Unicode characters to Namdhinggo SIL L legacy font characters. 
              The output displays as Unicode characters using Namdhinggo SIL font, but when copied, it copies the actual English characters (A, B, C, etc.) that the legacy font uses.
            </p>
        </div>
      </div>
    </div>
  );
};

// Unicode to Kirat Converter Component (kept for backward compatibility)
const UnicodeConverterPage = () => {
  return <UnicodeConverterContent />;
};

// Footer Component with modern design
const Footer = () => {
  return (
    <footer className="border-t border-gray-200/50 dark:border-gray-700/50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl py-8 mt-auto transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
        <p className="text-base md:text-lg text-gray-600 dark:text-gray-400 font-namdhinggo">
          Built with ❤️ by{' '}
          <a 
            href="https://codebundles.com" 
            className="font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent hover:from-blue-700 hover:to-indigo-700 dark:hover:from-blue-300 dark:hover:to-indigo-300 transition-all"
            target="_blank"
            rel="noopener noreferrer"
          >
            codebundles.com
          </a>
        </p>
      </div>
    </footer>
  );
};

// Main App Component
const App = () => {
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    // Check localStorage first, then default to light mode
    const savedTheme = localStorage.getItem('kirat-keyboard-theme') as 'light' | 'dark' | null;
    if (savedTheme) return savedTheme;
    return 'light';
  });

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('kirat-keyboard-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  return (
    <div className="flex flex-col min-h-screen bg-background dark:bg-gray-900 transition-colors duration-300">
      <main className="flex-1">
        <KiratLimbuKeyboard theme={theme} toggleTheme={toggleTheme} />
      </main>
      <Footer />
    </div>
  );
};

export default App;